import type { GraphViewNodeProps } from "@/components/GraphView/GraphView";
import GraphNode from "@/components/RuleGraph_FullRelease/components/DebuggerGraphNode";
import TriggerNode from "@/components/RuleGraph_FullRelease/components/TriggerNode";
import { useParsedGraph } from "@/pages/models/release/GraphContext";
import styled from "@emotion/styled";
import Icon from "@imminently/decisively-ui/Icon";
import Button from "@imminently/decisively-ui/Button";
import { Typography } from "@mui/material";
import { Handle, Position } from "@xyflow/react";
import React from "react";
import { useHistory } from "react-router-dom";

const MappingNodeContainer = styled.div`
  padding: 0.5rem 0.75rem;
  min-width: 160px;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--card);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const MappingNode = (props: GraphViewNodeProps) => {
  const { data } = props;
  const history = useHistory();
  const node = data.node as { id?: string; name?: string; mode?: "json" | "csv"; mappingId?: string };
  const mode = node.mode ?? "json";
  const mappingId =
    node.mappingId ?? (typeof node.id === "string" && node.id.startsWith("mapping:") ? node.id.slice(8) : undefined);
  const ModeIcon =
    mode === "csv"
      ? (p: React.SVGAttributes<SVGSVGElement>) => (
          <Icon
            {...p}
            name="view-chart"
          />
        )
      : (p: React.SVGAttributes<SVGSVGElement>) => (
          <Icon
            {...p}
            name="code"
          />
        );

  const handleOpenMapping = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (mappingId) {
        history.push(`/build/mapping?mappingId=${encodeURIComponent(mappingId)}`);
      }
    },
    [history, mappingId],
  );

  return (
    <MappingNodeContainer>
      <Handle
        type="target"
        position={Position.Left}
        id="mapping"
      />
      <ModeIcon style={{ width: 20, height: 20, flexShrink: 0, opacity: 0.8 }} />
      <Typography
        variant="body2"
        noWrap
        style={{ flex: 1, minWidth: 0 }}
      >
        {node.name ?? "Mapping"}
      </Typography>
      {mappingId ? (
        <Button icon
          size="sm"
          onClick={handleOpenMapping}          label="Open mapping"
          style={{ padding: 4 }}
        >
          <Icon name="open-in-new" />
        </Button>
      ) : null}
      <Handle
        type="source"
        position={Position.Right}
        id="data"
      />
    </MappingNodeContainer>
  );
};

const DecisionFlowNode = (props: GraphViewNodeProps) => {
  const parsedGraph = useParsedGraph();
  const { data } = props;
  const graphNode = parsedGraph.node(data.node.id);

  if (data.node.type === "mapping") {
    return <MappingNode {...props} />;
  }

  if (!graphNode?.id) {
    // might happen if you're building in the doc view and there's an inline graph on the screen already
    return null;
  }

  if (data.node.type === "attribute") {
    return !parsedGraph ? null : (
      <GraphNode
        {...props}
        horizontal={true}
        graph={parsedGraph as any}
        data={{
          ...data,
          node: graphNode,
        }}
        isDecisionFlow={true}
      />
    );
  }

  return <TriggerNode {...props} />;
};

export default DecisionFlowNode;
